import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { ConfirmModalComponent } from '../../../../shared/modals/confirm-modal/confirm-modal.component';
import {
  AutoDistributionOptions,
  autoDistributionOptionsFromRecords,
  DEFAULT_AUTO_DISTRIBUTION_OPTIONS,
  DistributionStrategy,
  LimitationType,
  normalizeOrderOverlapMinutes,
  ORDER_OVERLAP_MINUTES_MAX,
  ORDER_OVERLAP_MINUTES_MIN,
  SectorOption,
} from '../../sector-options';
import { SectorOptionsService } from '../../sector-options.service';

@Component({
  selector: 'app-sector-distribution-options',
  templateUrl: './sector-distribution-options.component.html',
  styleUrls: ['./sector-distribution-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SectorDistributionOptionsComponent implements OnChanges, OnDestroy {
  @Input() sectorId: number | string | null = null;

  public options: AutoDistributionOptions = Object.assign({}, DEFAULT_AUTO_DISTRIBUTION_OPTIONS);
  public records: SectorOption[] = [];
  public loading = false;
  public saving = false;
  public deleting = false;
  public loadError: string | null = null;
  public actionError: string | null = null;
  public successMessage: string | null = null;
  public readonly overlapMinutesMin = ORDER_OVERLAP_MINUTES_MIN;
  public readonly overlapMinutesMax = ORDER_OVERLAP_MINUTES_MAX;

  public readonly strategies: { value: DistributionStrategy; label: string; hint: string }[] = [
    {
      value: 'minimize_total_travel_time',
      label: 'Минимум времени в пути',
      hint: 'Минимальным количеством людей, минимальным расстоянием',
    },
    { value: 'balanced_distribution', label: 'Равномерное распределение', hint: 'Равное количество заявок на человека' },
    { value: 'priority_based', label: 'По приоритету заявок', hint: 'Учитывая приоритет аварийных заявок' },
  ];

  public readonly limitationTypes: { value: LimitationType; label: string; hint: string | null }[] = [
    { value: 1, label: 'Отключено', hint: null },
    {
      value: 2,
      label: 'Гибкая стратегия',
      hint: 'Приоритет отдаётся СИ, которые закреплены за районом выполнения заявки. Также СИ может привлекаться на соседние районы.',
    },
    {
      value: 3,
      label: 'Жесткая стратегия',
      hint: 'Приоритет отдаётся СИ, которые закреплены за районом выполнения заявки. СИ может выполнять заявки только в своём районе.',
    },
  ];

  private destroyed$ = new Subject<void>();
  private reload$ = new Subject<void>();

  constructor(private sectorOptionsService: SectorOptionsService, private modalService: NgbModal) {}

  get hasSavedOptions(): boolean {
    return this.records.length > 0;
  }

  get busy(): boolean {
    return this.loading || this.saving || this.deleting;
  }

  /** При выключенном автоматическом распределении остальные настройки недоступны */
  get fieldsDisabled(): boolean {
    return this.busy || !this.options.autoassigned;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.sectorId) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.reload$.next();
    this.reload$.complete();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public load(): void {
    this.reload$.next();
    this.resetMessages();
    this.loadError = null;
    this.records = [];
    this.options = Object.assign({}, DEFAULT_AUTO_DISTRIBUTION_OPTIONS);

    if (!this.sectorId) {
      return;
    }

    this.loading = true;
    this.sectorOptionsService
      .getAutoDistributionOptions(Number(this.sectorId))
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.reload$),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: (records) => this.applyRecords(records),
        error: (error) => {
          if (error && error.status === 404) {
            this.applyRecords([]);
            return;
          }
          this.loadError = this.errorMessage(error, 'Не удалось загрузить настройки автоматического распределения.');
        },
      });
  }

  public setAutoassigned(value: boolean): void {
    this.options.autoassigned = value;
    this.resetMessages();
  }

  public setAllowedOrderOverlapMinutes(value: unknown): void {
    this.options.allowedOrderOverlapMinutes = normalizeOrderOverlapMinutes(value);
    this.resetMessages();
  }

  /** Значение вне диапазона или пустое поле приводим к допустимому и показываем его в поле */
  public onAllowedOrderOverlapMinutesChange(event: Event): void {
    const input = event.target as HTMLInputElement;

    this.setAllowedOrderOverlapMinutes(input.value === '' ? ORDER_OVERLAP_MINUTES_MIN : input.value);
    input.value = String(this.options.allowedOrderOverlapMinutes);
  }

  public setAssignAfterResult(value: boolean): void {
    this.options.assignAfterResult = value;
    this.resetMessages();
  }

  public setDistributionStrategy(value: DistributionStrategy): void {
    this.options.distributionStrategy = value;
    this.resetMessages();
  }

  public setLimitationType(value: LimitationType): void {
    this.options.limitationType = value;
    this.resetMessages();
  }

  public save(): void {
    if (this.busy || this.loadError || !this.sectorId) {
      return;
    }

    const isCreate = !this.hasSavedOptions;
    this.resetMessages();
    this.saving = true;

    this.sectorOptionsService
      .saveAutoDistributionOptions(Number(this.sectorId), this.records, this.options)
      .pipe(
        finalize(() => (this.saving = false)),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: (records) => {
          this.applyRecords(records);
          this.successMessage = isCreate ? 'Настройки созданы.' : 'Настройки сохранены.';
        },
        error: (error) => {
          this.actionError = this.errorMessage(error, 'Не удалось сохранить настройки автоматического распределения.');
          // Часть записей могла сохраниться — перечитываем, чтобы не создавать дубли
          this.refreshRecordsSilently();
        },
      });
  }

  public confirmDelete(): void {
    if (this.busy || !this.hasSavedOptions) {
      return;
    }

    const confirmDialog = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    confirmDialog.componentInstance.title = 'Удаление настроек';
    confirmDialog.componentInstance.description =
      'Сохранённые настройки автоматического распределения участка будут удалены. Продолжить?';
    confirmDialog.componentInstance.successButtonName = 'Да, удалить';
    confirmDialog.componentInstance.abortButtonName = 'Отмена';

    confirmDialog.result.then(
      () => this.delete(),
      () => {}
    );
  }

  private delete(): void {
    this.resetMessages();
    this.deleting = true;

    this.sectorOptionsService
      .deleteAutoDistributionOptions(this.records)
      .pipe(
        finalize(() => (this.deleting = false)),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: () => {
          this.applyRecords([]);
          this.successMessage = 'Настройки удалены. Будут использоваться значения по умолчанию.';
        },
        error: (error) => {
          this.actionError = this.errorMessage(error, 'Не удалось удалить настройки автоматического распределения.');
          this.refreshRecordsSilently();
        },
      });
  }

  private refreshRecordsSilently(): void {
    if (!this.sectorId) {
      return;
    }

    this.sectorOptionsService
      .getAutoDistributionOptions(Number(this.sectorId))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (records) => (this.records = records),
        error: () => {},
      });
  }

  private applyRecords(records: SectorOption[]): void {
    this.records = records || [];
    this.options = autoDistributionOptionsFromRecords(this.records);
    // «Предпросмотр» недоступен: на участке всегда «Распределить и Сохранить»
    this.options.assignAfterResult = true;
  }

  private resetMessages(): void {
    this.actionError = null;
    this.successMessage = null;
  }

  private errorMessage(error: any, fallback: string): string {
    if (error && error.status === 403) {
      return 'Недостаточно прав для работы с настройками участка.';
    }

    const data = error && error.error && error.error.data;
    const errors = data && (data.number_value || data.char_value || data.name || data.sector || data.group || data.non_field_errors);
    const metadataMessage = error && error.error && error.error.metadata && error.error.metadata.message;

    return (Array.isArray(errors) ? errors.join(' ') : errors) || metadataMessage || fallback;
  }
}
